<template>
  <div class="timeline-component bg-white rounded-lg border-2 border-gray-400 p-6">
    <!-- Timeline Controls Header -->
    <div class="mb-4">
      <!-- Three Section Layout -->
      <div v-if="showControls && curveFeatures?.length > 0" class="flex justify-center mb-3">
        <div class="flex items-stretch gap-12">
          <!-- Left Section: Selection Dropdown -->
          <div class="timeline-section-box">
            <div class="flex flex-col items-center gap-3">
              <!-- Selection Dropdown -->
              <div class="relative">
                <!-- Debug info -->
                <StandardBlueDropdown
                  v-if="viewMode === 'curve' && curveFeatures && curveFeatures.length > 0"
                  :model-value="selectedCurveIndex"
                  @update:modelValue="onCurveDropdownSelect"
                  :options="curveDropdownOptions || []"
                  :suffix-right="true"
                  class="w-40"
                />
                <StandardPurpleDropdown
                  v-else-if="viewMode === 'day' && dayData && dayData.length > 0"
                  :model-value="selectedDayIndex"
                  @update:modelValue="onDayDropdownSelect"
                  :options="dayDropdownOptions || []"
                  :suffix-right="true"
                  class="w-40"
                />
                <div v-else class="w-40 h-8 bg-gray-100 rounded border flex items-center justify-center">
                  <span class="text-xs text-gray-500">Loading...</span>
                </div>
              </div>
              
              <!-- Re-align Button -->
              <div class="mt-1">
                <button
                  @click="realignCurrentSelection"
                  :disabled="!canRealign"
                  class="w-full px-3 py-1.5 text-xs font-medium rounded-md transition-colors duration-200"
                  :class="canRealign 
                    ? (viewMode === 'day' 
                        ? 'bg-purple-100 text-purple-700 hover:bg-purple-200 border border-purple-300' 
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200 border border-blue-300')
                    : 'bg-gray-100 text-gray-400 border border-gray-300 cursor-not-allowed'"
                >
                  {{ realignButtonText }}
                </button>
              </div>
            </div>
          </div>
          
          <!-- Middle Section: Relabel Controls (only visible in curve mode) -->
          <div v-if="viewMode === 'curve'" class="flex items-center justify-center">
            <div class="timeline-section-box max-w-md w-full">
              <!-- Quality Status Message -->
              <div class="text-sm text-gray-700 mb-3 text-center leading-relaxed">
                <span v-if="!isCurrentlyOverridden" class="text-xs italic text-gray-600">
                  Quality based on flag thresholds
                </span>
                <span v-else class="block">
                  Threshold-Based Quality Label:
                  <br>
                  <span class="font-bold text-black">Overridden to {{ isCurrentlyHighQuality ? 'High' : 'Low' }} Quality</span>
                </span>
              </div>
              
              <!-- Relabel Button -->
              <button
                @click="showRelabelModal"
                :disabled="processing"
                class="px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200"
                :class="relabelButtonClasses"
              >
                {{ relabelButtonText }}
              </button>
            </div>
          </div>
          
          <!-- Right Section: Flag Summary (only visible in curve mode) -->
          <div v-if="viewMode === 'curve'" class="flex items-center justify-center">
            <FlagSummary :flags="currentCurveFlags" container-class="timeline-section-box bg-blue-50" />
          </div>
          
          <!-- Feature Summary Section -->
          <div>
            <CurveFeatureSummary
              :view-mode="viewMode"
              :show-details="showDetails"
              :curve-data="getCurrentCurve"
              :day-data="selectedDayWithMetrics"
              :session-id="sessionId"
              @curve-relabeled="handleCurveRelabeled"
              @curve-relabel-error="handleCurveRelabelError"
            />
          </div>
        </div>
      </div>
      
      <!-- Fallback for no curves -->
      <div v-if="showControls && !curveFeatures?.length" class="mb-2">
        <div class="text-sm text-gray-500">{{ timelineStatusMessage }}</div>
      </div>
    </div>
    
    <!-- Interactive Timeline -->
    <div class="relative mb-2 timeline-container mt-16" style="min-height: 84px;">
      <!-- Timeline Track -->
      <div class="relative h-10 bg-gray-200 rounded-lg border border-gray-300 timeline-track">
        <!-- Visual indicator for draggable area -->
        <div class="absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 opacity-50 pointer-events-none"></div>
        
        <!-- Dynamic Timeline Markers -->
        <div class="absolute inset-0 pointer-events-none">
          <!-- Date lines (when spanning multiple days) -->
          <div v-if="timelineSpansMultipleDays" class="absolute inset-0">
            <div 
              v-for="(marker, index) in dateMarkers" 
              :key="index"
              class="absolute top-0 h-full flex flex-col items-center"
              :style="{ left: marker.percent + '%' }"
            >
              <div class="w-px h-4 bg-gray-400"></div>
              <div class="text-xs text-gray-500 mt-1 whitespace-nowrap">{{ marker.label }}</div>
            </div>
          </div>
          
          <!-- Hour lines (when within single day) -->
          <div v-else class="absolute inset-0">
            <div 
              v-for="(marker, index) in hourMarkers" 
              :key="index"
              class="absolute top-0 h-full flex flex-col items-center"
              :style="{ left: marker.percent + '%' }"
            >
              <div class="w-px h-3 bg-gray-300"></div>
              <div class="text-xs text-gray-500 mt-1 whitespace-nowrap">{{ marker.label }}</div>
            </div>
          </div>
        </div>
        
        <!-- Selected Range Highlight -->
        <div 
          class="absolute h-full bg-blue-200 opacity-50 selected-range pointer-events-none"
          :style="{
            left: `${selectedRangePercent.start}%`,
            width: `${selectedRangePercent.end - selectedRangePercent.start}%`
          }"
        ></div>
        
        <!-- Start Pointer -->
        <div 
          class="absolute top-0 transform -translate-x-1/2 timeline-pointer start-pointer transition-all duration-200"
          :class="{
            'cursor-ew-resize': isInteractive && !isFrozen,
            'cursor-not-allowed opacity-60': !isInteractive || isFrozen,
            'dragging': isDragging && dragType === 'start'
          }"
          :style="{ left: `${selectedRangePercent.start}%` }"
          @mousedown="isInteractive && startDragging('start')"
          @touchstart="isInteractive && startDragging('start')"
        >
          <!-- Center line indicator -->
          <div class="w-1 h-10 bg-blue-800 rounded-full"></div>
          <!-- Label with timestamp -->
          <div class="absolute -top-12 left-1/2 transform -translate-x-1/2 text-xs font-medium text-blue-600 bg-white px-2 py-1 rounded shadow-sm whitespace-normal border border-blue-200 pointer-events-none text-center">
            <div>Start</div>
            <div class="font-mono text-[10px] leading-tight text-gray-600">{{ formatDateDisplay(startTime) }}</div>
            <div class="font-mono text-[10px] leading-tight text-gray-600">{{ formatTimeDisplay(startTime) }}</div>
          </div>
        </div>
        
        <!-- End Pointer -->
        <div 
          class="absolute top-0 transform -translate-x-1/2 timeline-pointer end-pointer transition-all duration-200"
          :class="{
            'cursor-ew-resize': isInteractive && !isFrozen,
            'cursor-not-allowed opacity-60': !isInteractive || isFrozen,
            'dragging': isDragging && dragType === 'end'
          }"
          :style="{ left: `${selectedRangePercent.end}%` }"
          @mousedown="isInteractive && startDragging('end')"
          @touchstart="isInteractive && startDragging('end')"
        >
          <!-- Center line indicator -->
          <div class="w-1 h-10 bg-red-800 rounded-full"></div>
          <!-- Label with timestamp -->
          <div class="absolute -top-12 left-1/2 transform -translate-x-1/2 text-xs font-medium text-red-600 bg-white px-2 py-1 rounded shadow-sm whitespace-normal border border-red-200 pointer-events-none text-center">
            <div>End</div>
            <div class="font-mono text-[10px] leading-tight text-gray-600">{{ formatDateDisplay(endTime) }}</div>
            <div class="font-mono text-[10px] leading-tight text-gray-600">{{ formatTimeDisplay(endTime) }}</div>
          </div>
        </div>
        
        <!-- Global Freeze Overlay - covers entire timeline when frozen -->
        <div 
          v-if="isFrozen"
          class="absolute inset-0 bg-white bg-opacity-80 z-50 flex items-center justify-center rounded-lg"
          style="pointer-events: all;"
        >
                  <div class="text-center">
          <div class="inline-block animate-spin mr-2 text-blue-600 text-lg">⟳</div>
          <div class="text-blue-600 font-medium">{{ freezeMessage || 'Timeline Frozen' }}</div>
        </div>
        </div>
      </div>
      
      <!-- Timeline Labels with navigation buttons -->
      <div class="grid grid-cols-3 items-center text-xs text-gray-600 mt-2 select-none">
        <!-- Left side: Earliest -->
        <div class="justify-self-start flex flex-col items-start gap-1">
          <span>Earliest: {{ earliestTime ? new Date(earliestTime).toLocaleDateString('en-US') + ' ' + new Date(earliestTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : 'N/A' }}</span>
        </div>
        
        <!-- Center: Alignment badge with navigation buttons -->
        <div class="justify-self-center flex items-center gap-2" v-if="alignmentLabel">
          <!-- Back button -->
          <button
            v-if="navigationButtonsAvailable"
            @click="shiftTimelineBack"
            :disabled="!canShiftBack"
            class="arrow-button-left transition-colors duration-200 px-3 py-1 text-xs font-medium w-10"
            :class="canShiftBack 
              ? 'bg-gray-100 text-black hover:bg-gray-200' 
              : 'bg-gray-50 text-gray-300 cursor-not-allowed'"
          >
            12h
          </button>
          
          <!-- Alignment badge -->
          <span :class="['inline-flex items-center px-2 py-1 rounded-full font-medium border', alignmentClass]">
            {{ alignmentLabel }}
          </span>
          
          <!-- Forward button -->
          <button
            v-if="navigationButtonsAvailable"
            @click="shiftTimelineForward"
            :disabled="!canShiftForward"
            class="arrow-button-right transition-colors duration-200 px-3 py-1 text-xs font-medium w-10"
            :class="canShiftForward 
              ? 'bg-gray-100 text-black hover:bg-gray-200' 
              : 'bg-gray-50 text-gray-300 cursor-not-allowed'"
          >
            12h
          </button>
        </div>
        
        <!-- Right side: Latest -->
        <div class="justify-self-end flex flex-col items-end gap-1">
          <span>Latest: {{ latestTime ? new Date(latestTime).toLocaleDateString('en-US') + ' ' + new Date(latestTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : 'N/A' }}</span>
        </div>
      </div>
    </div>
    
    <!-- Status Messages - Always show when provided by parent -->
    <div v-if="freezeMessage" class="mt-4 pt-4 border-t border-gray-200">
      <!-- Show unified message from parent -->
      <div class="text-sm text-blue-600 text-center">
        {{ freezeMessage }}
      </div>
    </div>

    <!-- Relabel Confirmation Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
        <div class="text-center mb-4">
          <div class="flex justify-center mb-2">
            <div class="w-8 h-8 rounded-full flex items-center justify-center" :class="modalIconClasses">
              <span class="text-lg">{{ modalIcon }}</span>
            </div>
          </div>
          <h3 class="text-lg font-medium text-gray-900">{{ modalTitle }}</h3>
        </div>
        
        <div class="mb-6">
          <p class="text-sm text-gray-600 mb-4">{{ modalDescription }}</p>
          
          <!-- Combined status display -->
          <div class="flex justify-center items-center">
            <div class="font-medium px-3 py-1 rounded-md" :class="currentStatusClasses">
              {{ currentQualityText }}
            </div>
            <div class="text-gray-400 mx-3 text-lg">→</div>
            <div class="font-medium px-3 py-1 rounded-md" :class="newStatusClasses">
              {{ newQualityText }}
            </div>
          </div>
        </div>
        
        <div class="flex justify-center space-x-3 mt-8">
          <button
            @click="cancelRelabel"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            @click="confirmRelabel"
            :disabled="processing"
            class="px-4 py-2 text-sm font-medium text-white rounded-md transition-colors"
            :class="confirmButtonClasses"
          >
            {{ processing ? 'Processing...' : (isCurrentlyOverridden ? 'Confirm Undo' : 'Confirm Override') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StandardBlueDropdown from './StandardBlueDropdown.vue'
import StandardPurpleDropdown from './StandardPurpleDropdown.vue'
import CurveFeatureSummary from './CurveFeatureSummary.vue'
import FlagSummary from './FlagSummary.vue'
import { dataRevisionService } from '../utils/dataRevisionService.js'
export default {
  name: 'TimelineComponent',
  components: { StandardBlueDropdown, StandardPurpleDropdown, CurveFeatureSummary, FlagSummary },
  props: {
    // Data range from biosensor processed data
    earliestTime: {
      type: [String, Date],
      required: false,
      default: null
    },
    latestTime: {
      type: [String, Date],
      required: false,
      default: null
    },
    // View mode: 'curve' or 'day'
    viewMode: {
      type: String,
      default: 'curve',
      validator: (value) => ['curve', 'day'].includes(value)
    },
    // Curve features for curve-based positioning
    curveFeatures: {
      type: Array,
      default: () => []
    },
    // Current timeline state
    startTime: {
      type: [String, Date],
      required: true
    },
    endTime: {
      type: [String, Date],
      required: true
    },
    // Maximum time range allowed (default 24 hours)
    maxTimeRangeHours: {
      type: Number,
      default: 24
    },
    // Whether timeline is interactive
    isInteractive: {
      type: Boolean,
      default: true
    },
    // Whether timeline is frozen (e.g., during data processing)
    isFrozen: {
      type: Boolean,
      default: false
    },
    // Freeze message to display
    freezeMessage: {
      type: String,
      default: ''
    },
    // Optional alignment label/class from parent (for centered badge)
    alignmentLabel: {
      type: String,
      default: ''
    },
    alignmentClass: {
      type: String,
      default: ''
    },
    // Interaction message when not interactive
    interactionMessage: {
      type: String,
      default: ''
    },
    // Whether to allow initialization events (prevents premature event emission)
    allowInitializationEvents: {
      type: Boolean,
      default: false
    },
    // Map of curve IDs to their respective curve features
    curveTimelineMap: {
      type: Object,
      default: () => {}
    },
    showControls: {
      type: Boolean,
      default: true
    },
    showDetails: {
      type: Boolean,
      default: true
    },
    // Current selected curve index from parent
    selectedCurveIndex: {
      type: [String, Number],
      default: ''
    },
    // Day-level data for day selection
    dayData: {
      type: Array,
      default: () => []
    },
    // Current selected day index from parent
    selectedDayIndex: {
      type: [String, Number],
      default: ''
    },
    // Current curve flags from parent
    currentCurveFlags: {
      type: Array,
      default: () => []
    },
    // Session ID for API calls
    sessionId: {
      type: String,
      default: null
    }

  },
  emits: ['timeline-change', 'curve-selection-change', 'day-selection-change', 'curve-relabeled', 'curve-relabel-error'],
  data() {
    return {
      isDragging: false,
      dragType: null,
      selectedRangePercent: { start: 0, end: 100 },
      timelineRange: { start: null, mid: null, end: null },
      showCurveDropdown: false,
      processing: false,
      showModal: false
    }
  },
  computed: {
    curveDropdownOptions() {
      // format: left = "Curve n", right = start date/time
      if (!this.curveFeatures || !Array.isArray(this.curveFeatures)) {
        return []
      }
      
      const options = this.curveFeatures.map((curve, index) => {
        const right = this.curveTimelineMap && this.curveTimelineMap[index]
          ? this.curveTimelineMap[index].startFormatted
          : null
        
        const curveValid = Number(curve?.CURVE_VALID) || 0
        const peripheryValid = Number(curve?.PERIPHERY_VALID) || 0
        const regionValid = Number(curve?.REGION_VALID) || 0
        
        // Determine validity based on override status (same logic as isCurrentlyHighQuality)
        let isValid
        if (curve.override_flags === 1) {
          // Override active: use override decision (0 = valid/high quality, 1 = invalid/low quality)
          isValid = curve.FLAG_override_decision === 0
        } else {
          // No override: use threshold-based validity
          // A curve is high-quality if BOTH CURVE_VALID == 1 AND PERIPHERY_VALID == 1
          // This means no curve flags AND no periphery flags
          // Check if threshold_based_quality is a valid number (not null or undefined)
          if (curve.threshold_based_quality !== undefined && curve.threshold_based_quality !== null) {
            isValid = Number(curve.threshold_based_quality) === 1
          } else {
            // Use REGION_VALID which combines both CURVE_VALID and PERIPHERY_VALID
            // REGION_VALID = 1 only when both are 1 (no flags in either)
            if (regionValid !== undefined && regionValid !== null) {
              isValid = Number(regionValid) === 1
            } else {
              // Fallback: check both individually
              isValid = (curveValid === 1 && peripheryValid === 1)
            }
          }
        }
        
        const validityText = isValid === true ? 'High-Quality' : (isValid === false ? 'Low-Quality' : 'Unknown')
        const label = `Curve ${index + 1} (${validityText})`
        return { value: index.toString(), label, right }
      })
      
      return options
    },
    dayDropdownOptions() {
      // format: left = "Day n", right = date
      
      if (!this.dayData || !Array.isArray(this.dayData)) {
        return []
      }
      
      const options = this.dayData.map((day, index) => {
        // Use the actual start time from the day data instead of the date field
        // This ensures the displayed date matches the actual data range
        const startTime = day.begin_day || day.start_time
        const dayDate = startTime ? new Date(startTime) : new Date()
        const right = dayDate.toLocaleDateString('en-US', { 
          month: '2-digit', 
          day: '2-digit', 
          year: '2-digit' 
        })
        const label = `Day ${index + 1}`
        return { value: index.toString(), label, right }
      })
      
      return options
    },
    // Get current curve information from parent's curveTimelineMap
    getCurrentCurve() {
      if (!this.curveFeatures?.length) return null
      
      const curveIndex = this.selectedCurveIndex !== '' ? parseInt(this.selectedCurveIndex) : 0
      
      // Use curveTimelineMap prop if available, otherwise fallback to direct access
      if (this.curveTimelineMap && this.curveTimelineMap[curveIndex]) {
        return this.curveTimelineMap[curveIndex]
      }
      
      // Fallback to direct curveFeatures access
      const curve = this.curveFeatures[curveIndex]
      if (!curve) {
        return null
      }

      // For fallback, we need to format the dates manually since we don't have access to parent's formatDateTimeDisplay
      // API now returns unified format: '2021-05-22T15:51:00Z' - no conversion needed
      const startDate = new Date(curve.begin_CURVE)
      const endDate = new Date(curve.end_CURVE)
      
      const fallbackCurve = {
        id: curve.curve_id || curveIndex,
        index: curveIndex,
        start: curve.begin_CURVE,
        end: curve.end_CURVE,
        startDate: startDate,
        endDate: endDate,
        // Format dates for display (fallback formatting) - no timezone conversion
        startFormatted: startDate.toLocaleDateString('en-US') + ' ' + startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        endFormatted: endDate.toLocaleDateString('en-US') + ' ' + endDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        duration: curve.duration_CURVE,
        peak: curve.peak_CURVE,
        threshold: curve.curve_threshold || curve.threshold || 10,
        // Use REGION_VALID: High-Quality requires BOTH CURVE_VALID == 1 AND PERIPHERY_VALID == 1
        // This means no curve flags AND no periphery flags
        valid: (curve?.REGION_VALID !== undefined && curve?.REGION_VALID !== null) 
          ? Number(curve.REGION_VALID) === 1 
          : (Number(curve?.CURVE_VALID) === 1 && Number(curve?.PERIPHERY_VALID || 1) === 1),
        // Add missing properties that the template expects
        riseDuration: curve.rise_duration_CURVE,
        riseRate: curve.rise_rate_CURVE,
        fallDuration: curve.fall_duration_CURVE,
        fallRate: curve.fall_rate_CURVE,
        // CRITICAL: Add override-related properties for proper UI state
        override_flags: curve.override_flags || 0,
        FLAG_override_decision: curve.FLAG_override_decision,
        override_note: curve.override_note || '',
        threshold_based_quality: curve.threshold_based_quality,
        CURVE_VALID: curve.CURVE_VALID
      }
      
      console.log('🔍 Fallback curve object:', fallbackCurve)
      return fallbackCurve
    },
    
    // Timeline status message
    timelineStatusMessage() {
      if (!this.earliestTime || !this.latestTime) {
        return 'Timeline data not ready yet'
      }
      
      // Check if a day is selected
      if (this.selectedDayIndex !== '' && this.loadedDayData?.length > 0) {
        return 'Timeline focused on selected day'
      }
      
      if (!this.curveFeatures?.length) {
        // Check if original dataset is > 24 hours to determine message
        if (this.earliestTime && this.latestTime) {
          const fullStart = new Date(this.earliestTime)
          const fullEnd = new Date(this.latestTime)
          const fullDuration = (fullEnd.getTime() - fullStart.getTime()) / (1000 * 60 * 60)
          
          if (fullDuration > 24) {
            // Original dataset > 24 hours, so we're showing first 24 hours
            return 'No curves detected - showing first 24 hours'
          } else {
            // Original dataset ≤ 24 hours, so we're showing full range
            return 'No curves detected - using full data range'
          }
        }
        return 'No curves detected - using full data range'
      }
      
      // Check if timeline is showing full range or curve-focused view
      const fullStart = new Date(this.earliestTime)
      const fullEnd = new Date(this.latestTime)
      const timelineStart = new Date(this.startTime)
      const timelineEnd = new Date(this.endTime)
      
      // If timeline covers full range (within 1 minute tolerance), show full range message
      const startDiff = Math.abs(timelineStart.getTime() - fullStart.getTime())
      const endDiff = Math.abs(timelineEnd.getTime() - fullEnd.getTime())
      const tolerance = 60 * 1000 // 1 minute
      
      if (startDiff <= tolerance && endDiff <= tolerance) {
        return 'Timeline shows full data range (24-hour limit enforced)'
      } else {
        return 'Timeline focused on selected curve ±1 hour'
      }
    },

    // Day metrics calculations
    getDayDuration() {
      if (!this.selectedDay) return 'N/A'
      // Use device_turned_on_duration from day-level data
      const duration = Number(this.selectedDay.device_turned_on_duration || 0)
      return duration.toFixed(1)
    },

    getDayWornDuration() {
      if (!this.selectedDay) return 'N/A'
      // Use device_worn_duration from day-level data
      const duration = Number(this.selectedDay.device_worn_duration || 0)
      return duration.toFixed(1)
    },

    getDayExtremeNegativeDuration() {
      if (!this.selectedDay) return 'N/A'
      // Use extreme_negative_duration from day-level data
      const duration = Number(this.selectedDay.extreme_negative_duration || 0)
      return duration.toFixed(1)
    },

    // Get the currently selected day object
    selectedDay() {
      if (this.selectedDayIndex === '' || !this.dayData?.length) return null
      const dayIndex = parseInt(this.selectedDayIndex)
      const day = this.dayData[dayIndex]
      if (!day) return null
      
      // Ensure the day object has consistent date formatting
      return {
        ...day,
        // Use the actual start time for date display consistency
        displayDate: day.begin_day || day.start_time || day.date
      }
    },

    // Get the selected day with computed metrics for the feature summary
    selectedDayWithMetrics() {
      if (!this.selectedDay) return null
      
      return {
        ...this.selectedDay,
        deviceOnDuration: this.getDayDuration,
        deviceWornDuration: this.getDayWornDuration,
        extremeNegativeDuration: this.getDayExtremeNegativeDuration
      }
    },

    // Check if timeline spans multiple days
    timelineSpansMultipleDays() {
      if (!this.earliestTime || !this.latestTime) return false
      
      const start = new Date(this.earliestTime)
      const end = new Date(this.latestTime)
      
      // Check if start and end are on different days
      const startDay = start.toDateString()
      const endDay = end.toDateString()
      
      return startDay !== endDay
    },

    // Generate date markers for multi-day timelines
    dateMarkers() {
      if (!this.earliestTime || !this.latestTime) return []
      
      const start = new Date(this.earliestTime)
      const end = new Date(this.latestTime)
      const totalDuration = end.getTime() - start.getTime()
      
      const markers = []
      const minSpacing = 8 // Minimum spacing in percentage to avoid overlap
      
      // Start from the first complete day (next day after data start)
      // This avoids confusion where users can drag into non-existent data ranges
      const current = new Date(start)
      current.setDate(current.getDate() + 1)
      // Keep the original time from the start date, just increment the day
      current.setHours(start.getHours(), start.getMinutes(), start.getSeconds(), start.getMilliseconds())
      
      while (current < end) {
        const percent = ((current.getTime() - start.getTime()) / totalDuration) * 100
        // Scale the percent to fit within 0-95% range (since end marker is at 95%)
        const scaledPercent = (percent / 100) * 95
        
        // Only add if there's enough spacing from previous markers
        const hasEnoughSpace = markers.every(marker => Math.abs(marker.percent - scaledPercent) >= minSpacing)
        
        if (hasEnoughSpace) {
          markers.push({
            percent: scaledPercent,
            label: current.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
          })
        }
        
        current.setDate(current.getDate() + 1)
      }
      
      // Add end date marker only if there's enough space from the last intermediate marker
      const lastMarker = markers[markers.length - 1]
      const endPercent = 95
      const hasEnoughSpaceForEnd = !lastMarker || Math.abs(lastMarker.percent - endPercent) >= minSpacing
      
      if (hasEnoughSpaceForEnd) {
        // Show the date of the last complete day, not the end time
        const lastCompleteDay = new Date(end)
        lastCompleteDay.setDate(lastCompleteDay.getDate() - 1)
        markers.push({
          percent: endPercent,
          label: lastCompleteDay.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
        })
      }
      
      return markers
    },

    // Generate hour markers for single-day timelines
    hourMarkers() {
      if (!this.earliestTime || !this.latestTime) return []
      
      const start = new Date(this.earliestTime)
      const end = new Date(this.latestTime)
      const totalDuration = end.getTime() - start.getTime()
      
      const markers = []
      const minSpacing = 8 // Minimum spacing in percentage to avoid overlap
      
      // Add start time marker
      markers.push({
        percent: 0,
        label: start.toLocaleTimeString('en-US', { hour: 'numeric', hour12: false })
      })
      
      // Add intermediate hour markers with proper spacing
      const current = new Date(start)
      current.setHours(current.getHours() + 1, 0, 0, 0) // Next hour
      
      while (current < end) {
        const percent = ((current.getTime() - start.getTime()) / totalDuration) * 100
        // Scale the percent to fit within 0-95% range (since end marker is at 95%)
        const scaledPercent = (percent / 100) * 95
        
        // Only add if there's enough spacing from previous markers
        const hasEnoughSpace = markers.every(marker => Math.abs(marker.percent - scaledPercent) >= minSpacing)
        
        if (hasEnoughSpace) {
          markers.push({
            percent: scaledPercent,
            label: current.toLocaleTimeString('en-US', { hour: 'numeric', hour12: false })
          })
        }
        
        current.setHours(current.getHours() + 1)
      }
      
      // Add end time marker only if there's enough space from the last intermediate marker
      const lastMarker = markers[markers.length - 1]
      const endPercent = 95
      const hasEnoughSpaceForEnd = !lastMarker || Math.abs(lastMarker.percent - endPercent) >= minSpacing
      
      if (hasEnoughSpaceForEnd) {
        markers.push({
          percent: endPercent,
          label: end.toLocaleTimeString('en-US', { hour: 'numeric', hour12: false })
        })
      }
      
      return markers
    },
    
    // Button text based on current view mode
    realignButtonText() {
      return this.viewMode === 'curve' ? 'Re-align Curve' : 'Re-align Day'
    },
    
    // Check if re-align button should be enabled
    canRealign() {
      if (this.viewMode === 'curve') {
        // Enabled only when a curve is selected and we're NOT already in the standard curve view
        // Standard curve view label from parent is 'Curve ±1h View'
        return this.selectedCurveIndex !== '' && this.alignmentLabel !== 'Curve ±1h View'
      } else {
        // Enabled only when a day is selected and we're NOT already aligned to a day
        // In day mode, parent uses a date label for aligned or 'Custom Range' when not aligned
        return this.selectedDayIndex !== '' && this.alignmentLabel === 'Custom Range'
      }
    },
    
    // Check if navigation buttons should be available at all
    navigationButtonsAvailable() {
      if (!this.earliestTime || !this.latestTime) return false
      
      const start = new Date(this.earliestTime)
      const end = new Date(this.latestTime)
      const totalDurationHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60)
      
      // Only show navigation buttons if data spans more than 24 hours
      return totalDurationHours > 24
    },
    
    // Determine shift amount - always 12 hours when buttons are available
    shiftAmountHours() {
      return this.navigationButtonsAvailable ? 12 : 0
    },
    
    // Text for shift amount display
    shiftAmountText() {
      return this.navigationButtonsAvailable ? '12h' : ''
    },
    
    // Check if we can shift timeline backward
    canShiftBack() {
      if (!this.navigationButtonsAvailable || !this.earliestTime || !this.startTime) return false
      
      const earliest = new Date(this.earliestTime)
      const currentStart = new Date(this.startTime)
      
      // Can shift back if current start is not at the earliest possible time
      return currentStart.getTime() > earliest.getTime()
    },
    
    // Check if we can shift timeline forward
    canShiftForward() {
      if (!this.navigationButtonsAvailable || !this.latestTime || !this.endTime) return false
      
      const latest = new Date(this.latestTime)
      const currentEnd = new Date(this.endTime)
      
      // Can shift forward if current end is not at the latest possible time
      return currentEnd.getTime() < latest.getTime()
    },

    // Relabel functionality computed properties
    isCurrentlyHighQuality() {
      const currentCurve = this.getCurrentCurve
      if (!currentCurve) return false
      
      // Get the raw curve data to check validity values
      const curveIndex = currentCurve.index
      const rawCurve = this.curveFeatures?.[curveIndex]
      
      if (!rawCurve) {
        console.warn('🔍 isCurrentlyHighQuality: No raw curve data found')
        return false
      }
      
      const curveValid = Number(rawCurve?.CURVE_VALID) || 0
      const peripheryValid = Number(rawCurve?.PERIPHERY_VALID) || 0
      const regionValid = Number(rawCurve?.REGION_VALID) || 0
      const overrideFlags = Number(rawCurve?.override_flags) || 0
      
      console.log(`🔍 isCurrentlyHighQuality for curve ${curveIndex}:`, {
        CURVE_VALID: curveValid,
        PERIPHERY_VALID: peripheryValid,
        REGION_VALID: regionValid,
        override_flags: overrideFlags,
        threshold_based_quality: rawCurve.threshold_based_quality
      })
      
      // Check if there's an override decision
      if (overrideFlags === 1) {
        const result = Number(rawCurve.FLAG_override_decision) === 0
        console.log(`  → Using override: ${result ? 'High' : 'Low'}`)
        return result // 0 = valid/high quality
      }
      
      // Otherwise use threshold-based validity
      // A curve is high-quality if BOTH CURVE_VALID == 1 AND PERIPHERY_VALID == 1
      // This means no curve flags AND no periphery flags
      // Check if threshold_based_quality is a valid number (not null or undefined)
      if (rawCurve.threshold_based_quality !== undefined && rawCurve.threshold_based_quality !== null) {
        const result = Number(rawCurve.threshold_based_quality) === 1
        console.log(`  → Using threshold_based_quality: ${result ? 'High' : 'Low'}`)
        return result
      }
      
      // Use REGION_VALID which combines both CURVE_VALID and PERIPHERY_VALID
      if (regionValid !== undefined && regionValid !== null) {
        const result = Number(regionValid) === 1
        console.log(`  → Using REGION_VALID: ${result ? 'High' : 'Low'} (CURVE_VALID=${curveValid}, PERIPHERY_VALID=${peripheryValid})`)
        return result
      }
      
      // Fallback: check both individually
      const result = (curveValid === 1 && peripheryValid === 1)
      console.log(`  → Using fallback (CURVE_VALID && PERIPHERY_VALID): ${result ? 'High' : 'Low'}`)
      return result
    },

    relabelButtonText() {
      const currentCurve = this.getCurrentCurve
      if (!currentCurve) return 'Override Quality Label'
      
      const isOverridden = this.isCurrentlyOverridden
      const buttonText = isOverridden ? 'Undo Override' : 'Override Quality Label'
      
      return buttonText
    },

    relabelButtonClasses() {
      if (this.processing) {
        return 'bg-gray-100 text-gray-400 border border-gray-300 cursor-not-allowed'
      }

      const isOverridden = this.isCurrentlyOverridden
      
      return {
        // Base classes - always start with light gray
        'border': true,
        'bg-gray-100 text-gray-700 border-gray-300': true,
        // Dynamic hover colors - green for undo (if overridden), blue for override (if not)
        'hover:bg-green-100 hover:text-green-700 hover:border-green-300': isOverridden,
        'hover:bg-blue-100 hover:text-blue-700 hover:border-blue-300': !isOverridden,
        // Hover effects
        'hover:shadow-sm': true
      }
    },

    // Check if curve is currently overridden (differs from threshold-based assessment)
    isCurrentlyOverridden() {
      const currentCurve = this.getCurrentCurve
      if (!currentCurve) return false
      
      // Simple check: override_flags === 1 means it's overridden
      return currentCurve.override_flags === 1
    },

    // Modal computed properties
    modalTitle() {
      const isOverridden = this.isCurrentlyOverridden
      return isOverridden ? 'Undo Quality Override' : 'Override Quality Label'
    },

    modalDescription() {
      const isOverridden = this.isCurrentlyOverridden
      const currentQuality = this.isCurrentlyHighQuality
      
      if (isOverridden) {
        return 'Are you sure you want to undo the override and return to the threshold-based quality assessment?'
      } else {
        const newQuality = currentQuality ? 'Low' : 'High'
        return `Are you sure you want to override the current quality and mark this curve as ${newQuality} Quality?`
      }
    },

    modalIcon() {
      const isHighQuality = this.isCurrentlyHighQuality
      return isHighQuality ? '⚠️' : '✅'
    },

    modalIconClasses() {
      const isHighQuality = this.isCurrentlyHighQuality
      return isHighQuality 
        ? 'bg-red-100 text-red-600' 
        : 'bg-green-100 text-green-600'
    },

    currentQualityText() {
      return this.isCurrentlyHighQuality ? 'High Quality' : 'Low Quality'
    },

    newQualityText() {
      const isOverridden = this.isCurrentlyOverridden
      const currentCurve = this.getCurrentCurve
      
      if (isOverridden) {
        // Undoing override returns to threshold-based assessment
        // Show what the threshold-based quality actually is
        let thresholdQuality
        // Check if threshold_based_quality is a valid number (not null or undefined)
        if (currentCurve && currentCurve.threshold_based_quality !== undefined && currentCurve.threshold_based_quality !== null) {
          thresholdQuality = Number(currentCurve.threshold_based_quality) === 1
        } else {
          // Use REGION_VALID (or both CURVE_VALID and PERIPHERY_VALID)
          const rawCurve = this.curveFeatures?.[currentCurve.index]
          if (rawCurve) {
            const regionValid = Number(rawCurve?.REGION_VALID) || 0
            if (regionValid !== undefined && regionValid !== null) {
              thresholdQuality = regionValid === 1
            } else {
              const curveValid = Number(rawCurve?.CURVE_VALID) || 0
              const peripheryValid = Number(rawCurve?.PERIPHERY_VALID) || 0
              thresholdQuality = (curveValid === 1 && peripheryValid === 1)
            }
          } else {
            thresholdQuality = false
          }
        }
        return thresholdQuality ? 'High Quality' : 'Low Quality'
      } else {
        // Overriding flips the quality
        return this.isCurrentlyHighQuality ? 'Low Quality' : 'High Quality'
      }
    },

    currentStatusClasses() {
      return this.isCurrentlyHighQuality 
        ? 'bg-green-100 text-green-700' 
        : 'bg-red-100 text-red-700'
    },

    newStatusClasses() {
      const isOverridden = this.isCurrentlyOverridden
      const currentCurve = this.getCurrentCurve
      
      if (isOverridden) {
        // Show color based on what the threshold-based quality is
        let thresholdQuality
        // Check if threshold_based_quality is a valid number (not null or undefined)
        if (currentCurve && currentCurve.threshold_based_quality !== undefined && currentCurve.threshold_based_quality !== null) {
          thresholdQuality = Number(currentCurve.threshold_based_quality) === 1
        } else {
          // Use REGION_VALID (or both CURVE_VALID and PERIPHERY_VALID)
          const rawCurve = this.curveFeatures?.[currentCurve.index]
          if (rawCurve) {
            const regionValid = Number(rawCurve?.REGION_VALID) || 0
            if (regionValid !== undefined && regionValid !== null) {
              thresholdQuality = regionValid === 1
            } else {
              const curveValid = Number(rawCurve?.CURVE_VALID) || 0
              const peripheryValid = Number(rawCurve?.PERIPHERY_VALID) || 0
              thresholdQuality = (curveValid === 1 && peripheryValid === 1)
            }
          } else {
            thresholdQuality = false
          }
        }
        return thresholdQuality 
          ? 'bg-green-100 text-green-700' 
          : 'bg-red-100 text-red-700'
      } else {
        return this.isCurrentlyHighQuality 
          ? 'bg-red-100 text-red-700' 
          : 'bg-green-100 text-green-700'
      }
    },

    confirmButtonClasses() {
      const isOverridden = this.isCurrentlyOverridden
      return {
        'bg-green-600 hover:bg-green-700': isOverridden,
        'bg-blue-600 hover:bg-blue-700': !isOverridden,
        'cursor-not-allowed opacity-50': this.processing
      }
    }
  },
  watch: {
    // Watch for changes in start/end times to update percentages
    startTime: {
      handler(newVal, oldVal) {
        // Validate that new startTime is within bounds and before endTime
        if (newVal && this.earliestTime && this.latestTime) {
          const newStart = new Date(newVal)
          const earliest = new Date(this.earliestTime)
          const latest = new Date(this.latestTime)
          const currentEnd = new Date(this.endTime)
          
          // Check bounds
          if (newStart < earliest || newStart > latest) {
            console.warn('⚠️ TimelineComponent: startTime is outside bounds, clamping:', {
              startTime: newVal,
              earliestTime: this.earliestTime,
              latestTime: this.latestTime
            })
            
            // Clamp to bounds
            const clampedStart = newStart < earliest ? earliest : (newStart > latest ? latest : newStart)
            this.$emit('timeline-change', {
              startTime: this.formatNaive(clampedStart),
              endTime: this.formatNaive(currentEnd),
              source: 'boundary-clamp-start'
            })
            return
          }
          
          // Check that start is before end
          if (newStart >= currentEnd) {
            console.warn('⚠️ TimelineComponent: startTime is not before endTime, adjusting:', {
              startTime: newVal,
              endTime: this.endTime
            })
            
            // Adjust endTime to be after startTime (maintain minimum gap)
            const minGapMs = 30 * 60 * 1000 // 30 minutes minimum gap
            const adjustedEnd = new Date(newStart.getTime() + minGapMs)
            
            // Ensure adjusted end doesn't exceed latest
            const finalEnd = adjustedEnd > latest ? latest : adjustedEnd
            
            this.$emit('timeline-change', {
              startTime: this.formatNaive(newStart),
              endTime: this.formatNaive(finalEnd),
              source: 'start-end-order-fix'
            })
            return
          }
        }
        
        this.updateTimelinePercentages()
      },
      immediate: true
    },
    endTime: {
      handler(newVal, oldVal) {
        // Validate that new endTime is within bounds and after startTime
        if (newVal && this.earliestTime && this.latestTime) {
          const newEnd = new Date(newVal)
          const earliest = new Date(this.earliestTime)
          const latest = new Date(this.latestTime)
          const currentStart = new Date(this.startTime)
          
          // Check bounds
          if (newEnd < earliest || newEnd > latest) {
            console.warn('⚠️ TimelineComponent: endTime is outside bounds, clamping:', {
              endTime: newVal,
              earliestTime: this.earliestTime,
              latestTime: this.latestTime
            })
            
            // Clamp to bounds
            const clampedEnd = newEnd < earliest ? earliest : (newEnd > latest ? latest : newEnd)
            this.$emit('timeline-change', {
              startTime: this.formatNaive(currentStart),
              endTime: this.formatNaive(clampedEnd),
              source: 'boundary-clamp-end'
            })
            return
          }
          
          // Check that end is after start
          if (newEnd <= currentStart) {
            console.warn('⚠️ TimelineComponent: endTime is not after startTime, adjusting:', {
              startTime: this.startTime,
              endTime: newVal
            })
            
            // Adjust startTime to be before endTime (maintain minimum gap)
            const minGapMs = 30 * 60 * 1000 // 30 minutes minimum gap
            const adjustedStart = new Date(newEnd.getTime() - minGapMs)
            
            // Ensure adjusted start doesn't go below earliest
            const finalStart = adjustedStart < earliest ? earliest : adjustedStart
            
            this.$emit('timeline-change', {
              startTime: this.formatNaive(finalStart),
              endTime: this.formatNaive(newEnd),
              source: 'end-start-order-fix'
            })
            return
          }
        }
        
        this.updateTimelinePercentages()
      },
      immediate: true
    },
    // Watch for changes in earliest/latest times to update timeline range
    earliestTime: {
      handler(newVal) {
        this.updateTimelineRange()
      },
      immediate: true
    },
    latestTime: {
      handler(newVal) {
        this.updateTimelineRange()
      },
      immediate: true
    },
    // Watch for changes in selected curve index to update timeline
    selectedCurveIndex: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal && newVal !== '' && this.curveFeatures?.length > 0) {
          this.setTimelineFromSpecificCurve(parseInt(newVal))
        }
      },
      immediate: false
    },
    // Watch for changes in selected day index to update timeline
    selectedDayIndex: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal && newVal !== '' && this.dayData?.length > 0) {
          const selectedDay = this.getDayByIndex(parseInt(newVal))
          if (selectedDay) {
            this.setTimelineFromDay(selectedDay)
          }
        }
      },
      immediate: false
    },
    // Watch for changes in curveTimelineMap
    curveTimelineMap: {
      handler(newVal, oldVal) {
        
        // Force update to ensure computed properties recalculate
        this.$forceUpdate()
      },
      deep: true, // ✅ Watch for nested property changes
      immediate: false
    },
    // Watch for changes in curveFeatures prop
    curveFeatures: {
      handler(newVal, oldVal) {
        // Watcher for curveFeatures changes
      },
      immediate: true
    }
  },
  methods: {
    // Re-align timeline to current selection
    realignCurrentSelection() {
      if (this.viewMode === 'curve') {
        if (this.selectedCurveIndex !== '') {
          this.setTimelineFromSpecificCurve(parseInt(this.selectedCurveIndex))
        }
      } else {
        if (this.selectedDayIndex !== '') {
          const selectedDay = this.getDayByIndex(parseInt(this.selectedDayIndex))
          if (selectedDay) {
            this.setTimelineFromDay(selectedDay)
          }
        }
      }
    },

    onCurveDropdownSelect(val) {
      this.$emit('curve-selection-change', {
        curveIndex: parseInt(val),
        curve: this.getCurveByIndex(parseInt(val))
      })
    },
    
    onDayDropdownSelect(val) {
      const dayIndex = parseInt(val)
      const selectedDay = this.getDayByIndex(dayIndex)
      
      this.$emit('day-selection-change', {
        dayIndex: dayIndex,
        day: selectedDay
      })
      
      // If a day is selected, update timeline to show that day's range
      if (selectedDay) {
        this.setTimelineFromDay(selectedDay)
      }
    },

    // Handle curve relabel events from CurveFeatureSummary
    handleCurveRelabeled(eventData) {
      // Emit to parent component
      this.$emit('curve-relabeled', eventData)
    },

    handleCurveRelabelError(eventData) {
      console.error('❌ Curve relabel error:', eventData)
      // Emit to parent component
      this.$emit('curve-relabel-error', eventData)
    },

    // Relabel functionality methods
    showRelabelModal() {
      this.showModal = true
    },

    cancelRelabel() {
      this.showModal = false
    },

    async confirmRelabel() {
      const currentCurve = this.getCurrentCurve
      if (!currentCurve || this.processing) {
        return
      }

      try {
        this.processing = true
        
        const curveId = currentCurve.id || currentCurve.index
        const isCurrentlyOverridden = this.isCurrentlyOverridden
        const currentQuality = this.isCurrentlyHighQuality
        
        // Get threshold-based quality from stored value (set when override was first applied)
        // If no override has been applied yet, use current CURVE_VALID
        let thresholdBasedQuality
        // Check if threshold_based_quality is a valid number (not null or undefined)
        if (currentCurve.threshold_based_quality !== undefined && currentCurve.threshold_based_quality !== null) {
          // Use stored threshold-based quality
          thresholdBasedQuality = Number(currentCurve.threshold_based_quality) === 1
        } else {
          // No override yet, so current quality IS the threshold-based quality
          // Use REGION_VALID (or both CURVE_VALID and PERIPHERY_VALID)
          const rawCurve = this.curveFeatures?.[currentCurve.index]
          if (rawCurve) {
            const regionValid = Number(rawCurve?.REGION_VALID) || 0
            if (regionValid !== undefined && regionValid !== null) {
              thresholdBasedQuality = regionValid === 1
            } else {
              const curveValid = Number(rawCurve?.CURVE_VALID) || 0
              const peripheryValid = Number(rawCurve?.PERIPHERY_VALID) || 0
              thresholdBasedQuality = (curveValid === 1 && peripheryValid === 1)
            }
          } else {
            thresholdBasedQuality = false
          }
        }
        
        let newQuality, overrideDecision, override_flags, note
        
        if (isCurrentlyOverridden) {
          // UNDO OVERRIDE: Return to threshold-based quality
          newQuality = thresholdBasedQuality
          overrideDecision = thresholdBasedQuality ? 0 : 1
          override_flags = 0  // Clear override flag
          note = 'Override removed - returned to threshold-based assessment'
          console.log(`🔄 Undoing override for curve ${curveId} - returning to threshold-based quality: ${thresholdBasedQuality ? 'High' : 'Low'}`)
        } else {
          // APPLY OVERRIDE: Flip the current quality
          newQuality = !currentQuality
          overrideDecision = newQuality ? 0 : 1
          override_flags = 1  // Set override flag
          note = `Manual relabel: ${newQuality ? 'High' : 'Low'} Quality`
          console.log(`🔄 Applying override to curve ${curveId} from ${currentQuality ? 'High' : 'Low'} to ${newQuality ? 'High' : 'Low'} Quality`)
        }
        
        console.log(`🔄 Override flags: ${override_flags}`)
        
        // Persist to backend FIRST — only update frontend on success
        await this.persistCurveOverrideToBackend(curveId, override_flags, overrideDecision, note)

        // Backend succeeded — now safe to update frontend state
        this.updateFrontendCurveOverride(curveId, overrideDecision, override_flags, note)
        
        this.showModal = false
        
        this.$emit('curve-relabeled', {
          curveId,
          newQuality,
          overrideDecision,
          override_flags,
          note
        })
        
        console.log('✅ Curve relabeled successfully in frontend and backend')
        
      } catch (error) {
        console.error('❌ Error relabeling curve:', error)
        this.$emit('curve-relabel-error', {
          curveId: currentCurve.id || currentCurve.index,
          error: error.message
        })
      } finally {
        this.processing = false
      }
    },

    updateFrontendCurveOverride(curveId, overrideDecision, override_flags, note) {
      // Update sessionStorage
      const storedResults = sessionStorage.getItem('processingResults')
      if (storedResults) {
        const results = JSON.parse(storedResults)
        if (results.curve_features && Array.isArray(results.curve_features)) {
          const curveIndex = results.curve_features.findIndex(c => 
            (c.curve_id === curveId) || (results.curve_features.indexOf(c) === curveId)
          )
          
          if (curveIndex !== -1) {
            const curve = results.curve_features[curveIndex]
            
            // Use the override_flags value passed from confirmRelabel
            if (override_flags === 0) {
              // Clear override flags - returning to threshold-based assessment
              // Restore from stored threshold_based_quality if it exists
              const restoredQuality = curve.threshold_based_quality !== undefined 
                ? curve.threshold_based_quality 
                : (overrideDecision === 0 ? 1 : 0)
              
              results.curve_features[curveIndex].override_flags = 0
              results.curve_features[curveIndex].FLAG_override_decision = undefined
              results.curve_features[curveIndex].override_note = ''
              results.curve_features[curveIndex].CURVE_VALID = restoredQuality
              
              // Recalculate REGION_VALID: REGION_VALID = 1 only when both CURVE_VALID == 1 AND PERIPHERY_VALID == 1
              const peripheryValid = curve.PERIPHERY_VALID !== undefined && curve.PERIPHERY_VALID !== null 
                ? Number(curve.PERIPHERY_VALID) 
                : 1
              const curveValid = restoredQuality
              if (curveValid !== null && curveValid !== undefined && peripheryValid !== null && peripheryValid !== undefined) {
                results.curve_features[curveIndex].REGION_VALID = (curveValid === 1 && peripheryValid === 1) ? 1 : 0
              }
              
              // Clear the stored threshold_based_quality
              delete results.curve_features[curveIndex].threshold_based_quality
              
              console.log('✅ Cleared override flags - returned to threshold-based assessment:', {
                curveIndex,
                restoredQuality,
                CURVE_VALID: results.curve_features[curveIndex].CURVE_VALID,
                REGION_VALID: results.curve_features[curveIndex].REGION_VALID
              })
            } else {
              // Set override flags - applying manual override
              // Store the original threshold-based quality BEFORE overriding (if not already stored)
              if (curve.threshold_based_quality === undefined) {
                results.curve_features[curveIndex].threshold_based_quality = curve.CURVE_VALID
                console.log('💾 Stored original threshold_based_quality:', curve.CURVE_VALID)
              }
              
              results.curve_features[curveIndex].override_flags = 1
              results.curve_features[curveIndex].FLAG_override_decision = overrideDecision
              results.curve_features[curveIndex].override_note = note
              results.curve_features[curveIndex].CURVE_VALID = overrideDecision === 0 ? 1 : 0
              
              // Recalculate REGION_VALID: REGION_VALID = 1 only when both CURVE_VALID == 1 AND PERIPHERY_VALID == 1
              const peripheryValid = curve.PERIPHERY_VALID !== undefined && curve.PERIPHERY_VALID !== null 
                ? Number(curve.PERIPHERY_VALID) 
                : 1
              const curveValid = results.curve_features[curveIndex].CURVE_VALID
              if (curveValid !== null && curveValid !== undefined && peripheryValid !== null && peripheryValid !== undefined) {
                results.curve_features[curveIndex].REGION_VALID = (curveValid === 1 && peripheryValid === 1) ? 1 : 0
              }
              
              console.log('✅ Set override flags - applied manual override:', {
                curveIndex,
                overrideDecision,
                storedThreshold: results.curve_features[curveIndex].threshold_based_quality,
                newCURVE_VALID: results.curve_features[curveIndex].CURVE_VALID,
                REGION_VALID: results.curve_features[curveIndex].REGION_VALID
              })
            }
            
            // Save back to sessionStorage
            sessionStorage.setItem('processingResults', JSON.stringify(results))
          }
        }
      }
    },

    async persistCurveOverrideToBackend(curveId, override_flags, overrideDecision, note) {
      try {
        // Get session ID and s3_key from processing results
        const storedResults = sessionStorage.getItem('processingResults')
        if (!storedResults) {
          console.error('❌ No processing results found for backend persistence')
          return
        }
        
        const results = JSON.parse(storedResults)
        const sessionId = results.session_id
        const s3Key = results.s3_key
        
        // For prior results, session_id is null but s3_key is available
        if (!sessionId && !s3Key) {
          console.error('❌ No session ID or s3_key found for backend persistence')
          return
        }
        
        console.log('🔄 Persisting curve override to backend:', {
          sessionId,
          s3Key,
          curveId,
          override_flags,
          overrideDecision,
          note
        })
        
        const response = await dataRevisionService.overrideCurveQuality(
          sessionId,
          s3Key,
          curveId,
          override_flags,
          overrideDecision,
          note
        )
        
        if (response.success) {
          console.log('✅ Curve override persisted to backend successfully:', response.data)
          
          if (this.$parent && this.$parent.updateGlobalState) {
            this.$parent.updateGlobalState({ processingResults: results })
          }
        } else {
          console.error('❌ Backend persistence failed:', response.message)
          throw new Error(response.message || 'Backend persistence failed')
        }
        
      } catch (error) {
        console.error('❌ Error persisting curve override to backend:', error)
        this.$emit('curve-relabel-backend-error', {
          curveId,
          error: error.message
        })
        throw error
      }
    },

    
    // Helper method to get curve by index
    getCurveByIndex(index) {
      if (!this.curveFeatures?.length || index < 0 || index >= this.curveFeatures.length) return null
      
      // Use curveTimelineMap prop if available, otherwise fallback to direct access
      if (this.curveTimelineMap && this.curveTimelineMap[index]) {
        return this.curveTimelineMap[index]
      }
      
      // Fallback to direct curveFeatures access
      const curve = this.curveFeatures[index]
      if (!curve) return null
      
      const startDate = new Date(curve.begin_CURVE)
      const endDate = new Date(curve.end_CURVE)
      
      return {
        id: curve.curve_id || index,
        index: index,
        start: curve.begin_CURVE,
        end: curve.end_CURVE,
        startDate: startDate,
        endDate: endDate,
        startFormatted: startDate.toLocaleDateString('en-US') + ' ' + startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        endFormatted: endDate.toLocaleDateString('en-US') + ' ' + endDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
        duration: curve.duration_CURVE,
        peak: curve.peak_CURVE,
        threshold: curve.curve_threshold || curve.threshold || 10,
        // Use REGION_VALID: High-Quality requires BOTH CURVE_VALID == 1 AND PERIPHERY_VALID == 1
        // This means no curve flags AND no periphery flags
        valid: (curve?.REGION_VALID !== undefined && curve?.REGION_VALID !== null) 
          ? Number(curve.REGION_VALID) === 1 
          : (Number(curve?.CURVE_VALID) === 1 && Number(curve?.PERIPHERY_VALID || 1) === 1),
        // CRITICAL: Add override-related properties for proper UI state
        override_flags: curve.override_flags || 0,
        FLAG_override_decision: curve.FLAG_override_decision,
        override_note: curve.override_note || '',
        threshold_based_quality: curve.threshold_based_quality,
        CURVE_VALID: curve.CURVE_VALID
      }
    },
    
    // Helper method to get day by index
    getDayByIndex(index) {
      if (!this.dayData?.length || index < 0 || index >= this.dayData.length) return null
      
      const day = this.dayData[index]
      if (!day) return null
      
      console.log('🔍 getDayByIndex - Original day data:', day)
      console.log('🔍 getDayByIndex - Day keys:', Object.keys(day))
      console.log('🔍 getDayByIndex - start_time:', day.start_time)
      console.log('🔍 getDayByIndex - end_time:', day.end_time)
      
      // Handle different day data structures
      const startTime = day.begin_day || day.start_time
      const endTime = day.end_day || day.end_time
      const dateStr = day.date
      
      console.log('🔍 getDayByIndex - Using startTime:', startTime, 'endTime:', endTime, 'dateStr:', dateStr)
      
      return {
        id: day.day_id || index,
        index: index,
        start_time: startTime,
        end_time: endTime,
        date: dateStr,
        startDate: new Date(startTime),
        endDate: new Date(endTime),
        startFormatted: new Date(startTime).toLocaleDateString('en-US') + ' ' + new Date(startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        endFormatted: new Date(endTime).toLocaleDateString('en-US') + ' ' + new Date(endTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        duration: day.duration || ((new Date(endTime).getTime() - new Date(startTime).getTime()) / (1000 * 60 * 60))
      }
    },
    // Build a timezone-naive timestamp string (YYYY-MM-DDTHH:mm:ss)
    formatNaive(dateLike) {
      const d = dateLike instanceof Date ? dateLike : new Date(dateLike)
      const pad2 = (n) => String(n).padStart(2, '0')
      return (
        d.getFullYear() + '-' +
        pad2(d.getMonth() + 1) + '-' +
        pad2(d.getDate()) + 'T' +
        pad2(d.getHours()) + ':' +
        pad2(d.getMinutes()) + ':' +
        pad2(d.getSeconds())
      )
    },
    // Initialize timeline based on curve detection
    initializeCurveBasedTimeline() {
      if (this.curveFeatures?.length > 0) {
        return this.setTimelineFromCurves()
      }
      return false
    },
    
    // Set timeline based on curve detection
    setTimelineFromCurves() {
      if (this.curveFeatures?.length > 0) {
        // Use the selected curve index from prop, or default to 0
        const curveIndex = this.selectedCurveIndex !== '' ? parseInt(this.selectedCurveIndex) : 0
        const firstCurve = this.getCurveByIndex(curveIndex)
        
        if (firstCurve?.startDate && firstCurve?.endDate) {
          try {
            const curveStart = firstCurve.startDate
            const curveEnd = firstCurve.endDate
            
            if (isNaN(curveStart.getTime()) || isNaN(curveEnd.getTime())) {
              console.warn('Invalid curve timestamps:', { start: firstCurve.start, end: firstCurve.end })
              return false
            }
            
            // Set timeline to first curve ±1 hour
            const startTime = new Date(curveStart.getTime() - (1 * 60 * 60 * 1000))
            const endTime = new Date(curveEnd.getTime() + (1 * 60 * 60 * 1000))
            
            // Ensure we don't go beyond available data
            const fullStart = new Date(this.earliestTime)
            const fullEnd = new Date(this.latestTime)
            
            if (startTime < fullStart) startTime.setTime(fullStart.getTime())
            if (endTime > fullEnd) endTime.setTime(fullEnd.getTime())
            
            // Always emit timeline change for curve selection to update markers
            this.$emit('timeline-change', {
              startTime: this.formatNaive(startTime),
              endTime: this.formatNaive(endTime),
              source: 'curve-selection'
            })
            
            return true
          } catch (error) {
            console.error('Error setting timeline from curves:', error)
            return false
          }
        }
      }
      return false
    },
    
    // Select curve from dropdown
    selectCurveFromDropdown(curveIndex) {
      this.showCurveDropdown = false
      this.$emit('curve-selection-change', {
        curveIndex: parseInt(curveIndex),
        curve: this.getCurveByIndex(parseInt(curveIndex))
      })
    },
    
    // Select a specific curve
    selectCurve() {
      if (this.selectedCurveIndex === '') return
      
      const success = this.setTimelineFromSpecificCurve(this.selectedCurveIndex)
      if (success) {
        this.$emit('curve-selection-change', {
          curveIndex: parseInt(this.selectedCurveIndex),
          curve: this.getCurveByIndex(parseInt(this.selectedCurveIndex))
        })
      }
    },
    
    // Set timeline based on a specific curve index
    setTimelineFromSpecificCurve(curveIndex) {
      if (this.curveFeatures?.length > curveIndex) {
        // Get curve info using the helper method
        const selectedCurve = this.getCurveByIndex(curveIndex)
        
        if (selectedCurve?.startDate && selectedCurve?.endDate) {
          try {
            const curveStart = selectedCurve.startDate
            const curveEnd = selectedCurve.endDate
            
            console.log('🎯 Curve timestamps:', {
              curveStart: curveStart.toISOString(),
              curveEnd: curveEnd.toISOString(),
              curveDurationHours: ((curveEnd.getTime() - curveStart.getTime()) / (1000 * 60 * 60)).toFixed(2)
            })
            
            if (isNaN(curveStart.getTime()) || isNaN(curveEnd.getTime())) {
              console.warn('Invalid curve timestamps:', { start: selectedCurve.start, end: selectedCurve.end })
              return false
            }
            
            // Show selected curve ±1 hour
            const startTime = new Date(curveStart.getTime() - (1 * 60 * 60 * 1000))
            const endTime = new Date(curveEnd.getTime() + (1 * 60 * 60 * 1000))
            
            console.log('🎯 Timeline with ±1 hour padding:', {
              startTime: startTime.toISOString(),
              endTime: endTime.toISOString(),
              totalDurationHours: ((endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60)).toFixed(2)
            })
            
            // Ensure we don't go beyond available data
            const fullStart = new Date(this.earliestTime)
            const fullEnd = new Date(this.latestTime)
            
            if (startTime < fullStart) startTime.setTime(fullStart.getTime())
            if (endTime > fullEnd) endTime.setTime(fullEnd.getTime())
            
            console.log('🎯 Final timeline after bounds check:', {
              startTime: startTime.toISOString(),
              endTime: endTime.toISOString(),
              finalDurationHours: ((endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60)).toFixed(2)
            })
            
            // Always emit timeline change for curve selection to update markers
            console.log('🎯 Emitting timeline-change for curve selection')
            this.$emit('timeline-change', {
              startTime: this.formatNaive(startTime),
              endTime: this.formatNaive(endTime),
              source: 'curve-selection'
            })
            
            return true
          } catch (error) {
            console.error('Error setting timeline from specific curve:', error)
            return false
          }
        }
      }
      return false
    },
    
    // Set timeline based on day data
    setTimelineFromDay(day) {
      if (day && (day.start_time || day.startDate) && (day.end_time || day.endDate)) {
        try {
          console.log('🎯 Day object structure:', day)
          
          // Handle different day object structures
          const startTimeStr = day.start_time || day.startDate
          const endTimeStr = day.end_time || day.endDate
          
          console.log('🎯 Raw time strings:', { startTimeStr, endTimeStr })
          
          const dayStart = new Date(startTimeStr)
          const dayEnd = new Date(endTimeStr)
          
          console.log('🎯 Parsed dates:', {
            dayStart: dayStart.toISOString(),
            dayEnd: dayEnd.toISOString(),
            dayDurationHours: ((dayEnd.getTime() - dayStart.getTime()) / (1000 * 60 * 60)).toFixed(2)
          })
          
          if (isNaN(dayStart.getTime()) || isNaN(dayEnd.getTime())) {
            console.warn('Invalid day timestamps:', { start: startTimeStr, end: endTimeStr })
            return false
          }
          
          // Use the day's exact start and end times
          const startTime = new Date(dayStart.getTime())
          const endTime = new Date(dayEnd.getTime())
          
          console.log('🎯 Timeline set to day range:', {
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString(),
            totalDurationHours: ((endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60)).toFixed(2)
          })
          
          // Emit timeline change for day selection
          console.log('🎯 Emitting timeline-change for day selection')
          this.$emit('timeline-change', {
            startTime: this.formatNaive(startTime),
            endTime: this.formatNaive(endTime),
            source: 'day-selection'
          })
          
          return true
        } catch (error) {
          console.error('Error setting timeline from day:', error)
          return false
        }
      }
      return false
    },
    
    // Reset timeline to full range
    resetTimeline() {
      // Check if we're currently showing full range or curve focus
      const fullStart = new Date(this.earliestTime)
      const fullEnd = new Date(this.latestTime)
      const timelineStart = new Date(this.startTime)
      const timelineEnd = new Date(this.endTime)
      
      // If timeline covers full range (within 1 minute tolerance), focus back on curve or day
      const startDiff = Math.abs(timelineStart.getTime() - fullStart.getTime())
      const endDiff = Math.abs(timelineEnd.getTime() - fullEnd.getTime())
      const tolerance = 60 * 1000 // 1 minute
      
      if (startDiff <= tolerance && endDiff <= tolerance) {
        // Currently showing full range, focus back on selected curve or day
        if (this.selectedDayIndex !== '' && this.dayData?.length > 0) {
          // Focus back on selected day
          const selectedDay = this.getDayByIndex(parseInt(this.selectedDayIndex))
          if (selectedDay) {
            this.setTimelineFromDay(selectedDay)
          }
        } else if (this.selectedCurveIndex !== '') {
          // Focus back on selected curve
          this.setTimelineFromSpecificCurve(this.selectedCurveIndex)
        } else {
          this.setTimelineFromCurves()
        }
        return
      }
      
      // Otherwise, expand to full range
      let endTime = fullEnd
      
      // Check if the full range exceeds max hours and limit if necessary
      const fullRangeHours = (fullEnd.getTime() - fullStart.getTime()) / (1000 * 60 * 60)
      if (fullRangeHours > this.maxTimeRangeHours) {
        endTime = new Date(fullStart.getTime() + (this.maxTimeRangeHours * 60 * 60 * 1000))
      }
      
      // Emit curve selection change to reset to no selection
      this.$emit('curve-selection-change', {
        curveIndex: -1,
        curve: null
      })
      
      // Emit day selection change to reset to no selection
      this.$emit('day-selection-change', {
        dayIndex: -1,
        day: null
      })
      
      // Emit timeline change
      this.$emit('timeline-change', {
        startTime: this.formatNaive(fullStart),
        endTime: this.formatNaive(endTime),
        source: 'reset-to-full'
      })
    },
    
    // Public method to force reset to full range (can be called from parent)
    forceResetToFullRange() {
      console.log('🔄 Force reset to full range requested')
      
      const fullStart = new Date(this.earliestTime)
      const fullEnd = new Date(this.latestTime)
      
      // Check if the full range exceeds max hours and limit if necessary
      let endTime = fullEnd
      const fullRangeHours = (fullEnd.getTime() - fullStart.getTime()) / (1000 * 60 * 60)
      if (fullRangeHours > this.maxTimeRangeHours) {
        endTime = new Date(fullStart.getTime() + (this.maxTimeRangeHours * 60 * 60 * 1000))
      }
      
      // Emit curve selection change to reset to no selection
      this.$emit('curve-selection-change', {
        curveIndex: -1,
        curve: null
      })
      
      // Emit day selection change to reset to no selection
      this.$emit('day-selection-change', {
        dayIndex: -1,
        day: null
      })
      
      // Emit timeline change
      this.$emit('timeline-change', {
        startTime: this.formatNaive(fullStart),
        endTime: this.formatNaive(endTime),
        source: 'reset-to-full'
      })
    },
    
    // Toggle curve dropdown
    toggleCurveDropdown() {
      this.showCurveDropdown = !this.showCurveDropdown
    },
    
    // Update timeline percentages based on current start/end times
    updateTimelinePercentages() {
      if (!this.earliestTime || !this.latestTime) return
      
      const first = new Date(this.earliestTime)
      const last = new Date(this.latestTime)
      const totalDuration = last.getTime() - first.getTime()
      
      if (this.startTime && this.endTime) {
        const startDate = new Date(this.startTime)
        const endDate = new Date(this.endTime)
        
        this.selectedRangePercent.start = ((startDate.getTime() - first.getTime()) / totalDuration) * 100
        this.selectedRangePercent.end = ((endDate.getTime() - first.getTime()) / totalDuration) * 100
        
        // Ensure percentages are within bounds
        this.selectedRangePercent.start = Math.max(0, Math.min(100, this.selectedRangePercent.start))
        this.selectedRangePercent.end = Math.max(0, Math.min(100, this.selectedRangePercent.end))
        
        // Ensure minimum gap (reduced to allow closer positioning)
        if (this.selectedRangePercent.end - this.selectedRangePercent.start < 0.5) {
          this.selectedRangePercent.end = Math.min(100, this.selectedRangePercent.start + 0.5)
        }
      }
    },
    
    // Update timeline range display
    updateTimelineRange() {
      if (!this.earliestTime || !this.latestTime) return
      
      const first = new Date(this.earliestTime)
      const last = new Date(this.latestTime)
      
      this.timelineRange = {
        start: first,
        mid: new Date(first.getTime() + (last.getTime() - first.getTime()) / 2),
        end: last
      }
    },
    
    // Interactive Timeline Methods
    startDragging(type) {
      console.log('🎯 startDragging called:', {
        type,
        isInteractive: this.isInteractive,
        isFrozen: this.isFrozen,
        selectedRangePercent: this.selectedRangePercent
      })
      
      if (!this.isInteractive || this.isFrozen) {
        console.log('🎯 startDragging blocked:', { isInteractive: this.isInteractive, isFrozen: this.isFrozen })
        return
      }
      
      this.isDragging = true
      this.dragType = type
      
      console.log('🎯 Drag started:', { type, isDragging: this.isDragging })
      
      // Add event listeners
      document.addEventListener('mousemove', this.drag)
      document.addEventListener('touchmove', this.drag)
      document.addEventListener('mouseup', this.stopDragging)
      document.addEventListener('touchend', this.stopDragging)
    },
    
    drag(event) {
      if (!this.isDragging) return
      
      event.preventDefault()
      
      const timelineElement = event.target.closest('.bg-gray-200') || 
                             document.querySelector('.bg-gray-200')
      if (!timelineElement) return
      
      const rect = timelineElement.getBoundingClientRect()
      const clientX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX
      const percent = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100))
      
      // Calculate the 24-hour limit in percentage terms
      const maxRangePercent = this.calculateMaxRangePercent()
      
      if (this.dragType === 'start') {
        const newStart = Math.min(percent, this.selectedRangePercent.end - 0.5)
        const newEnd = this.selectedRangePercent.end
        
        // Check if dragging start would exceed 24-hour limit
        if (newEnd - newStart > maxRangePercent) {
          // If so, move both markers to maintain 24-hour distance
          // Allow start marker to move freely, carrying end marker with it
          this.selectedRangePercent.start = newStart
          this.selectedRangePercent.end = newStart + maxRangePercent
        } else {
          this.selectedRangePercent.start = newStart
        }
      } else if (this.dragType === 'end') {
        const newStart = this.selectedRangePercent.start
        const newEnd = Math.max(percent, this.selectedRangePercent.start + 0.5)
        
        // Check if dragging end would exceed 24-hour limit
        if (newEnd - newStart > maxRangePercent) {
          // If so, move both markers to maintain 24-hour distance
          this.selectedRangePercent.start = newEnd - maxRangePercent
          this.selectedRangePercent.end = newEnd
        } else {
          this.selectedRangePercent.end = newEnd
        }
      }
      
      // Force a visual update
      this.$forceUpdate()
    },
    
    stopDragging() {
      console.log('🎯 stopDragging called:', {
        isDragging: this.isDragging,
        dragType: this.dragType,
        selectedRangePercent: this.selectedRangePercent
      })
      
      this.isDragging = false
      this.dragType = null
      
      // Remove event listeners
      document.removeEventListener('mousemove', this.drag)
      document.removeEventListener('touchmove', this.drag)
      document.removeEventListener('mouseup', this.stopDragging)
      document.removeEventListener('touchend', this.stopDragging)
      
      console.log('🎯 Calling updateTimelineFromPercent after drag')
      // Update timeline from percentages
      this.updateTimelineFromPercent()
    },
    
    // Calculate the maximum range percentage based on 24-hour limit
    calculateMaxRangePercent() {
      if (!this.earliestTime || !this.latestTime) return 100
      
      const first = new Date(this.earliestTime)
      const last = new Date(this.latestTime)
      const totalDuration = last.getTime() - first.getTime()
      const maxRangeMs = this.maxTimeRangeHours * 60 * 60 * 1000
      
      return (maxRangeMs / totalDuration) * 100
    },
    
    // Update timeline from percentage positions
    updateTimelineFromPercent() {
      if (!this.earliestTime || !this.latestTime) return
      
      console.log('🎯 TimelineComponent: updateTimelineFromPercent called with:', {
        selectedRangePercent: this.selectedRangePercent,
        earliestTime: this.earliestTime,
        latestTime: this.latestTime
      })
      
      this.isTimelineAdjusting = true
      
      try {
        const first = new Date(this.earliestTime)
        const last = new Date(this.latestTime)
        
        // Validate dates
        if (isNaN(first.getTime()) || isNaN(last.getTime())) {
          console.warn('Invalid timeline dates:', { earliestTime: this.earliestTime, latestTime: this.latestTime })
          return
        }
        
        const totalDuration = last.getTime() - first.getTime()
        
        if (totalDuration <= 0) {
          console.warn('Invalid timeline duration:', totalDuration)
          return
        }
        
        const startTime = first.getTime() + (this.selectedRangePercent.start / 100) * totalDuration
        let endTime = first.getTime() + (this.selectedRangePercent.end / 100) * totalDuration
        
        // Check if the selected range exceeds max hours and limit if necessary
        const selectedRangeHours = (endTime - startTime) / (1000 * 60 * 60)
        if (selectedRangeHours > this.maxTimeRangeHours) {
          endTime = startTime + (this.maxTimeRangeHours * 60 * 60 * 1000)
        }
        
        // Validate the calculated times
        const startDate = new Date(startTime)
        const endDate = new Date(endTime)
        
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.warn('Invalid calculated timeline dates:', { startTime, endTime })
          return
        }
        
        // Emit timeline change with a small delay to prevent rapid-fire updates
        setTimeout(() => {
          this.$emit('timeline-change', {
            startTime: this.formatNaive(startDate),
            endTime: this.formatNaive(endDate),
            source: 'user-drag'
          })
        }, 50) // Small delay to prevent rapid updates
        
      } catch (error) {
        console.error('Error in updateTimelineFromPercent:', error)
      } finally {
        // Reset adjustment flag after a delay
        setTimeout(() => {
          this.isTimelineAdjusting = false
        }, 500)
      }
    },
    
    // Format time for display
    formatTimeDisplay(date) {
      if (!date) return '--'
      // API returns raw time, display as-is without timezone conversion
      const dateObj = date instanceof Date ? date : new Date(date)
      return dateObj.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false
      })
    },
    
    // Format date only
    formatDateDisplay(date) {
      if (!date) return '--'
      const d = date instanceof Date ? date : new Date(date)
      return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })
    },
    
    // Shift timeline backward by the determined amount
    shiftTimelineBack() {
      if (!this.canShiftBack) return
      
      const shiftMs = this.shiftAmountHours * 60 * 60 * 1000
      const currentStart = new Date(this.startTime)
      const currentEnd = new Date(this.endTime)
      const earliest = new Date(this.earliestTime)
      const latest = new Date(this.latestTime)
      
      // Calculate new times
      const newStart = new Date(currentStart.getTime() - shiftMs)
      const newEnd = new Date(currentEnd.getTime() - shiftMs)
      
      // Calculate new percentages
      const totalDuration = latest.getTime() - earliest.getTime()
      const newStartPercent = ((newStart.getTime() - earliest.getTime()) / totalDuration) * 100
      const newEndPercent = ((newEnd.getTime() - earliest.getTime()) / totalDuration) * 100
      
      console.log('🔄 Shifting timeline back by', this.shiftAmountHours, 'hours:', {
        oldStart: currentStart.toISOString(),
        oldEnd: currentEnd.toISOString(),
        newStart: newStart.toISOString(),
        newEnd: newEnd.toISOString(),
        newStartPercent: newStartPercent.toFixed(2),
        newEndPercent: newEndPercent.toFixed(2)
      })
      
      // Update the timeline percentages (this will trigger the existing marker movement pipeline)
      this.selectedRangePercent.start = Math.max(0, Math.min(100, newStartPercent))
      this.selectedRangePercent.end = Math.max(0, Math.min(100, newEndPercent))
      
      // Trigger the existing timeline update pipeline
      this.updateTimelineFromPercent()
    },
    
    // Shift timeline forward by the determined amount
    shiftTimelineForward() {
      if (!this.canShiftForward) return
      
      const shiftMs = this.shiftAmountHours * 60 * 60 * 1000
      const currentStart = new Date(this.startTime)
      const currentEnd = new Date(this.endTime)
      const earliest = new Date(this.earliestTime)
      const latest = new Date(this.latestTime)
      
      // Calculate new times
      const newStart = new Date(currentStart.getTime() + shiftMs)
      const newEnd = new Date(currentEnd.getTime() + shiftMs)
      
      // Calculate new percentages
      const totalDuration = latest.getTime() - earliest.getTime()
      const newStartPercent = ((newStart.getTime() - earliest.getTime()) / totalDuration) * 100
      const newEndPercent = ((newEnd.getTime() - earliest.getTime()) / totalDuration) * 100
      
      console.log('🔄 Shifting timeline forward by', this.shiftAmountHours, 'hours:', {
        oldStart: currentStart.toISOString(),
        oldEnd: currentEnd.toISOString(),
        newStart: newStart.toISOString(),
        newEnd: newEnd.toISOString(),
        newStartPercent: newStartPercent.toFixed(2),
        newEndPercent: newEndPercent.toFixed(2)
      })
      
      // Update the timeline percentages (this will trigger the existing marker movement pipeline)
      this.selectedRangePercent.start = Math.max(0, Math.min(100, newStartPercent))
      this.selectedRangePercent.end = Math.max(0, Math.min(100, newEndPercent))
      
      // Trigger the existing timeline update pipeline
      this.updateTimelineFromPercent()
    }

  },
  
  mounted() {
    
    // Initialize timeline
    this.updateTimelineRange()
    this.updateTimelinePercentages()
    
    // Initialize curve-based timeline if curves are available
    if (this.curveFeatures?.length > 0) {
      this.initializeCurveBasedTimeline()
    }

  },
  
  beforeUnmount() {
    // Clean up event listeners
    document.removeEventListener('mousemove', this.drag)
    document.removeEventListener('touchmove', this.drag)
    document.removeEventListener('mouseup', this.stopDragging)
    document.removeEventListener('touchend', this.stopDragging)
  }
}
</script>

<style scoped>
.timeline-component {
  width: 100%;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* Consistent box styling for all timeline sections */
.timeline-section-box {
  background-color: #f9fafb; /* bg-gray-50 */
  padding: 1rem; /* p-4 */
  border-radius: 0.25rem; /* rounded */
  border: 2px solid #9ca3af; /* border-2 border-gray-400 */
  text-align: center;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  min-height: 120px; /* Reduced height for more compact boxes */
}

.timeline-container {
  overflow: visible;
  margin-bottom: 0.25rem;
}

.timeline-track {
  overflow: visible;
  position: relative;
  height: 40px;
  background: linear-gradient(to right, #e5e7eb, #d1d5db);
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.timeline-pointer {
  transition: all 0.2s ease;
  position: relative;
  border: 3px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border-radius: 0.375rem;
}

.timeline-pointer::before {
  content: '';
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  background: transparent;
  cursor: ew-resize;
  border-radius: 0.5rem;
}

.timeline-pointer:hover::before {
  background: rgba(251, 191, 36, 0.1);
  border: 2px dashed rgba(251, 191, 36, 0.3);
}

.timeline-pointer:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  border-color: #fbbf24;
}

/* End pointer scales normally on hover */
.end-pointer:hover {
  transform: scale(1.1);
}

/* Start pointer shifts left on hover to avoid overlap */
.start-pointer:hover {
  transform: translateX(-8px) scale(1.1);
}

.timeline-pointer.dragging {
  transform: scale(1.1);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.4);
  z-index: 20;
}

.start-pointer.dragging {
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.4);
}

.end-pointer.dragging {
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.4);
}

.start-pointer {
  z-index: 15;
  position: absolute !important;
  top: 0 !important;
}

.start-pointer:hover {
  z-index: 25;
}

.end-pointer {
  z-index: 20;
  position: absolute !important;
  top: 0 !important;
}

.timeline-pointer.cursor-not-allowed {
  pointer-events: none;
  position: relative;
}

.timeline-pointer.cursor-not-allowed::after {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 0.125rem;
  pointer-events: none;
  z-index: 40;
  border: 2px solid rgba(156, 163, 175, 0.5);
}

.timeline-pointer.cursor-not-allowed::before {
  content: '⏸';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  color: #6b7280;
  z-index: 41;
  pointer-events: none;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.timeline-pointer > div:last-child {
  z-index: 25;
  pointer-events: none;
  white-space: nowrap;
  min-width: max-content;
  overflow: visible;
  text-overflow: clip;
}

.timeline-pointer > div {
  max-height: 48px;
  overflow: hidden;
}

.timeline-pointer > div:first-child {
  z-index: 30;
  max-height: 100%;
  overflow: hidden;
}

.selected-range {
  background: linear-gradient(to right, #93c5fd, #3b82f6);
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.3);
}

.timeline-track::-webkit-scrollbar {
  height: 8px;
}

.timeline-track::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.timeline-track::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.timeline-track::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Arrow-shaped buttons */
.arrow-button-left {
  position: relative;
  border: none;
  outline: none;
  clip-path: polygon(15% 0%, 100% 0%, 100% 100%, 15% 100%, 0% 50%);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: center;
  padding-right: 8px;
}

.arrow-button-left::before {
  content: '';
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  background: #9ca3af;
  clip-path: polygon(15% 0%, 100% 0%, 100% 100%, 15% 100%, 0% 50%);
  z-index: -1;
}

.arrow-button-left:hover::before {
  background: #6b7280;
}

.arrow-button-right {
  position: relative;
  border: none;
  outline: none;
  clip-path: polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  padding-left: 8px;
}

.arrow-button-right::before {
  content: '';
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  background: #9ca3af;
  clip-path: polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%);
  z-index: -1;
}

.arrow-button-right:hover::before {
  background: #6b7280;
}
</style>
